import { z } from "zod";
export const SYSTEM_SANS_FONT_STACK = [
    "-apple-system",
    "BlinkMacSystemFont",
    "\"Segoe UI\"",
    "Roboto",
    "\"Helvetica Neue\"",
    "Arial",
    "\"Noto Sans\"",
    "\"Liberation Sans\"",
    "sans-serif",
].join(", ");
export const SYSTEM_SERIF_FONT_STACK = [
    "Georgia",
    "\"Times New Roman\"",
    "Times",
    "serif",
].join(", ");
export const WIDGET_FONT_DEFINITIONS = [
    {
        id: "inter",
        label: "Inter",
        fontStack: `"Inter Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/inter/wght.css"],
    },
    {
        id: "geist",
        label: "Geist",
        fontStack: `"Geist Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/geist/wght.css"],
    },
    {
        id: "figtree",
        label: "Figtree",
        fontStack: `"Figtree Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/figtree/wght.css"],
    },
    {
        id: "manrope",
        label: "Manrope",
        fontStack: `"Manrope Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/manrope/wght.css"],
    },
    {
        id: "poppins",
        label: "Poppins",
        fontStack: `"Poppins", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: [
            "@fontsource/poppins/latin-300.css",
            "@fontsource/poppins/latin-300-italic.css",
            "@fontsource/poppins/latin-400.css",
            "@fontsource/poppins/latin-400-italic.css",
            "@fontsource/poppins/latin-500.css",
            "@fontsource/poppins/latin-500-italic.css",
            "@fontsource/poppins/latin-600.css",
            "@fontsource/poppins/latin-600-italic.css",
            "@fontsource/poppins/latin-700.css",
            "@fontsource/poppins/latin-700-italic.css",
        ],
    },
    {
        id: "source-serif-4",
        label: "Source Serif 4",
        fontStack: `"Source Serif 4 Variable", ${SYSTEM_SERIF_FONT_STACK}`,
        cssImports: ["@fontsource-variable/source-serif-4/wght.css"],
    },
    {
        id: "newsreader",
        label: "Newsreader",
        fontStack: `"Newsreader Variable", ${SYSTEM_SERIF_FONT_STACK}`,
        cssImports: ["@fontsource-variable/newsreader/wght.css"],
    },
    {
        id: "fraunces",
        label: "Fraunces",
        fontStack: `"Fraunces Variable", ${SYSTEM_SERIF_FONT_STACK}`,
        cssImports: ["@fontsource-variable/fraunces/wght.css"],
    },
    {
        id: "bricolage-grotesque",
        label: "Bricolage Grotesque",
        fontStack: `"Bricolage Grotesque Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/bricolage-grotesque/wght.css"],
    },
    {
        id: "geist-mono",
        label: "Geist Mono",
        fontStack: `"Geist Mono Variable", ${SYSTEM_SANS_FONT_STACK}`,
        cssImports: ["@fontsource-variable/geist-mono/wght.css"],
    },
];
export const WIDGET_FONT_REGISTRY = Object.fromEntries(WIDGET_FONT_DEFINITIONS.map(({ id, label, fontStack }) => [id, { id, label, fontStack }]));
const LEGACY_CANONICAL_FONT_STACKS = [
    ["inter", "Inter", SYSTEM_SANS_FONT_STACK],
    ["geist", "Geist", SYSTEM_SANS_FONT_STACK],
    ["figtree", "Figtree", SYSTEM_SANS_FONT_STACK],
    ["manrope", "Manrope", SYSTEM_SANS_FONT_STACK],
    ["poppins", "Atkinson Hyperlegible Next", SYSTEM_SANS_FONT_STACK],
    ["source-serif-4", "Source Serif 4", SYSTEM_SERIF_FONT_STACK],
    ["newsreader", "Newsreader", SYSTEM_SERIF_FONT_STACK],
    ["fraunces", "Fraunces", SYSTEM_SERIF_FONT_STACK],
    ["bricolage-grotesque", "Bricolage Grotesque", SYSTEM_SANS_FONT_STACK],
    ["geist-mono", "Geist Mono", SYSTEM_SANS_FONT_STACK],
];
const legacyFontStackReplacements = new Map(LEGACY_CANONICAL_FONT_STACKS.map(([fontId, legacyFamily, fallbackStack]) => [
    `"${legacyFamily}", ${fallbackStack}`,
    WIDGET_FONT_REGISTRY[fontId].fontStack,
]));
export const WIDGET_FONT_OPTIONS = Object.values(WIDGET_FONT_REGISTRY);
export const WIDGET_FONT_CATALOG = WIDGET_FONT_OPTIONS;
export const WidgetFontIdSchema = z.enum(Object.keys(WIDGET_FONT_REGISTRY));
const hostFamilyPattern = /^[\p{L}\p{N}][\p{L}\p{N} ._'()-]*$/u;
export const CatalogFontSelectionSchema = z.object({
    source: z.literal("catalog"),
    fontId: WidgetFontIdSchema,
});
export const HostFontSelectionSchema = z.object({
    source: z.literal("host"),
    family: z.string().trim().min(1).max(200).regex(hostFamilyPattern),
    fallback: z.enum(["sans-serif", "serif"]),
});
export const FontSelectionSchema = z.discriminatedUnion("source", [
    CatalogFontSelectionSchema,
    HostFontSelectionSchema,
]);
export function getCatalogFont(fontId) {
    return Object.hasOwn(WIDGET_FONT_REGISTRY, fontId)
        ? WIDGET_FONT_REGISTRY[fontId]
        : undefined;
}
/**
 * Upgrades persisted stacks emitted before Fontsource's variable families
 * gained the `Variable` suffix. Only complete former catalog stacks match;
 * custom host stacks remain host-controlled.
 */
export function normalizeLegacyWidgetFontStack(fontStack) {
    const normalized = fontStack.trim();
    return legacyFontStackReplacements.get(normalized) ?? normalized;
}
export function buildHostFontStack(family, fallback) {
    const fallbackStack = fallback === "serif"
        ? SYSTEM_SERIF_FONT_STACK
        : SYSTEM_SANS_FONT_STACK;
    return `"${family.replaceAll("\"", "\\\"")}", ${fallbackStack}`;
}
export function resolveFontSelection(selection) {
    if (selection.source === "host") {
        return { fontFamily: buildHostFontStack(selection.family, selection.fallback) };
    }
    const catalogFont = getCatalogFont(selection.fontId);
    return catalogFont
        ? { fontFamily: catalogFont.fontStack, fontId: catalogFont.id }
        : undefined;
}
//# sourceMappingURL=widget-fonts.js.map